import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Box } from '@mantine/core';
import { IconGripVertical } from '@tabler/icons-react';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EssayNode } from '../../hooks/useEssayApi';
import { EssayTreeNode, INDENT_PX } from './EssayTreeNode';
import { FlatNode, findNode, flatten, getProjection } from './essayTree';

/** Глубже DOCX-шаблон нумеровать не умеет: он описывает w:lvl 0..8
 *  (MAX_LIST_LEVEL в essayDocxExportService). */
const MAX_DEPTH = 8;

interface Props {
  nodes: EssayNode[];
  readOnly?: boolean;
  /** Плоская секция: перетаскивание меняет только порядок, уровень всегда 0. */
  allowChildren?: boolean;
  focusId?: string | null;
  onChangeText: (id: string, text: string) => void;
  onAddChild: (id: string) => void;
  onRemove: (id: string) => void;
  onAddSibling: (id: string) => void;
  onMove: (activeId: string, parentId: string | null, index: number) => void;
}

interface RowProps {
  row: FlatNode;
  depth: number;
  readOnly?: boolean;
  allowChildren: boolean;
  focusId?: string | null;
  onChangeText: (id: string, text: string) => void;
  onAddChild: (id: string) => void;
  onRemove: (id: string) => void;
  onAddSibling: (id: string) => void;
}

const SortableRow: React.FC<RowProps> = ({ row, depth, readOnly, ...rest }) => {
  const { t } = useTranslation();
  const {
    setNodeRef,
    setActivatorNodeRef,
    listeners,
    attributes,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: readOnly });

  const handle = readOnly ? null : (
    <ActionIcon
      ref={setActivatorNodeRef}
      variant="subtle"
      color="gray"
      mt={4}
      // Тащим только за ручку: перетаскивание за саму строку отняло бы у
      // textarea выделение текста.
      style={{ cursor: 'grab', touchAction: 'none' }}
      aria-label={t('essays.dragHandle')}
      {...attributes}
      {...listeners}
    >
      <IconGripVertical size={16} />
    </ActionIcon>
  );

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        // Перетаскиваемая строка не должна прятаться под соседями.
        zIndex: isDragging ? 1 : undefined,
        position: 'relative',
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <EssayTreeNode node={row} depth={depth} readOnly={readOnly} dragHandle={handle} {...rest} />
    </Box>
  );
};

/**
 * Секция эссе с перетаскиванием пунктов. Дерево разворачивается в плоский
 * список строк: вложенные drop-зоны не дают посчитать позицию, а линейный
 * список — даёт. Уровень при перетаскивании задаётся горизонтальным сдвигом,
 * дерево пересобирается уже в onMove.
 */
export const EssaySortableList: React.FC<Props> = ({
  nodes,
  readOnly,
  allowChildren = true,
  focusId,
  onMove,
  ...handlers
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [offsetX, setOffsetX] = useState(0);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    // Задержка на тач-устройствах: без неё палец на ручке начинал бы
    // перетаскивание вместо прокрутки списка.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const rows = useMemo(() => flatten(nodes), [nodes]);

  // Потомки едут вместе с узлом, поэтому на время перетаскивания они пропадают
  // из списка — иначе пункт можно было бы уронить внутрь самого себя, а позиции
  // считались бы по строкам, которые всё равно переедут.
  const visible = useMemo(() => {
    if (!activeId) return rows;
    const active = findNode(nodes, activeId);
    if (!active) return rows;
    const hidden = new Set(flatten(active.children).map((r) => r.id));
    return rows.filter((r) => !hidden.has(r.id));
  }, [rows, nodes, activeId]);

  const projection =
    activeId && overId
      ? getProjection(visible, activeId, overId, offsetX, INDENT_PX, allowChildren ? MAX_DEPTH : 0)
      : null;

  const reset = () => {
    setActiveId(null);
    setOverId(null);
    setOffsetX(0);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    setOverId(String(active.id));
    setOffsetX(0);
  };
  const handleDragMove = ({ delta }: DragMoveEvent) => setOffsetX(delta.x);
  const handleDragOver = ({ over }: DragOverEvent) => setOverId(over ? String(over.id) : null);
  const handleDragEnd = ({ over }: DragEndEvent) => {
    if (activeId && over && projection) {
      onMove(activeId, projection.parentId, projection.index);
    }
    reset();
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={visible.map((r) => r.id)} strategy={verticalListSortingStrategy}>
        {visible.map((row) => (
          <SortableRow
            key={row.id}
            row={row}
            // Перетаскиваемая строка показывает уровень, на который встанет,
            // пока её ещё держат — иначе сдвиг вправо ничем не отзывается.
            depth={row.id === activeId && projection ? projection.depth : row.depth}
            readOnly={readOnly}
            allowChildren={allowChildren}
            focusId={focusId}
            {...handlers}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
